import React from "react";
import PanelShell from "../shared/PanelShell";
import { fmtNumber, labelFor } from "../shared/format";

// CALL SHAPE — Tier 4, Phone only (V6 §E.14). A two-segment agent-versus-caller
// talk-ratio bar, with the by_stage set beneath as a small ranked list. A thin
// conversation-specific panel, not weather's StatPair (welded) or a radar (four
// dimensions on a spider chart encode nothing a bar does not).
//
// DATA, AND AN HONESTY NOTE. by_stage comes from fn_get_call_loss_histogram
// (`histogram.by_stage`) and is the ranked set. The agent talk ratio, per §E.14,
// lives on intelligence.timing — which is PER-CONVERSATION (the detail pane), not a
// page-level aggregate: none of the load-time payloads carries an aggregate talk
// ratio. So the two-segment bar renders ONLY when an aggregate ratio is actually
// present (`histogram.agent_talk_ratio`, defensively); otherwise the panel shows the
// by_stage set alone rather than inventing a ratio. This ambiguity was flagged for
// the backend; the panel reads what the payload gives and never derives a ratio.
//
// by_stage arrives LIVE as an object keyed by stage — {greeting: 24,
// first_question: 37, mid_answer: 30, after_answer: 52, not_abandoned: 90} (probed
// 2026-09-25). Reading it only as an array discarded the whole payload and showed
// "No data" on every Phone page. It is turned into rows in the payload's own key
// order with its own values — a reshape, not a derivation. The array form (entries
// carrying label/stage + calls/count/value) is still accepted.

const stageLabel = (s) => s?.label || s?.stage || s?.name || "";

/**
 * Normalise by_stage to a list of { stage, label, calls } rows. Accepts the live
 * object form ({ stage: count }) and the array form; anything else is no rows.
 */
const toStageRows = (byStage) => {
  if (Array.isArray(byStage)) return byStage;
  if (byStage && typeof byStage === "object") {
    return Object.entries(byStage)
      .filter(([, calls]) => calls != null)
      .map(([stage, calls]) => ({ stage, label: labelFor(stage), calls }));
  }
  return [];
};
const stageValue = (s) => Number(s?.calls ?? s?.count ?? s?.value ?? 0);

const TalkRatioBar = ({ ratio }) => {
  const agentPct = Math.round(Math.min(1, Math.max(0, Number(ratio))) * 100);
  const callerPct = 100 - agentPct;
  return (
    <div data-testid="talk-ratio">
      <div className="flex h-3 w-full overflow-hidden rounded-full" aria-hidden="true">
        <span className="bg-[#32B1FF]" style={{ width: `${agentPct}%` }} />
        <span className="bg-gray-300 dark:bg-gray-600" style={{ width: `${callerPct}%` }} />
      </div>
      <div className="flex justify-between mt-1">
        <span className="gi-label text-gray-500 dark:text-gray-400">Agent {agentPct}%</span>
        <span className="gi-label text-gray-500 dark:text-gray-400">Caller {callerPct}%</span>
      </div>
    </div>
  );
};

const CallShape = ({ panelKey = "voice_call_shape", say, histogram, loading = false }) => {
  const h = histogram || {};
  const byStage = toStageRows(h.by_stage);
  const ratio = h.agent_talk_ratio;
  const hasRatio = ratio != null;
  const hasData = hasRatio || byStage.length > 0;
  const widest = byStage.reduce((m, s) => Math.max(m, stageValue(s)), 0);

  return (
    <PanelShell panelKey={panelKey} label="Call shape" say={say} hasData={hasData} loading={loading}>
      <div data-testid="call-shape" className="space-y-4">
        {hasRatio && <TalkRatioBar ratio={ratio} />}
        {byStage.length > 0 && (
          <div className="space-y-1.5">
            {byStage.map((s, i) => {
              const v = stageValue(s);
              const pct = widest > 0 ? Math.max(2, Math.round((v / widest) * 100)) : 0;
              return (
                <div key={stageLabel(s) || i} className="flex items-center justify-between gap-3">
                  <span className="gi-body text-gray-900 dark:text-gray-100 truncate">{stageLabel(s)}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="hidden sm:block h-2 w-24 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden" aria-hidden="true">
                      <span className="block h-full rounded-full bg-[#32B1FF]" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="gi-body font-semibold tabular-nums text-gray-900 dark:text-gray-100 w-8 text-right">{fmtNumber(v)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PanelShell>
  );
};

export default CallShape;
